/**
 * Mock payment service — backed by the in-memory `mockPayments` fixtures.
 *
 * Mutations operate on a module-local copy so create/process/fail reflect in
 * subsequent reads within the same session (mirrors the customer mock pattern).
 */

import { mockPayments } from '@/data/mockPayments'
import { mockDelay } from '@/services/factory'
import {
  getMockSubscriptionInvoice,
  settleMockSubscriptionInvoice,
} from '@/services/tenant/tenant.mock'
import type { PaymentService } from './payments.service'
import type {
  Payment,
  PaymentStats,
  CreatePaymentInput,
  UpdatePaymentInput,
  OnlinePaymentInput,
  RazorpayCheckoutOrder,
  RazorpayCheckoutProof,
  RazorpaySubscriptionCheckoutOrder,
  RazorpaySubscriptionPaymentResult,
} from '@/types/payment.types'

let payments: Payment[] = [...mockPayments]
const onlineOrders = new Map<string, RazorpayCheckoutOrder>()
const subscriptionOrders = new Map<string, RazorpaySubscriptionCheckoutOrder>()

function findOrThrow(id: string): Payment {
  const found = payments.find((p) => p.id === id)
  if (!found) throw new Error('Payment not found')
  return found
}

function computeStats(rows: Payment[]): PaymentStats {
  return {
    totalPayments: rows.length,
    totalAmount: rows
      .filter((p) => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
    successfulPayments: rows.filter((p) => p.status === 'completed').length,
    failedPayments: rows.filter((p) => p.status === 'failed').length,
    pendingPayments: rows.filter(
      (p) => p.status === 'pending' || p.status === 'processing'
    ).length,
    currency: 'INR',
  }
}

export function createMockPaymentService(): PaymentService {
  return {
    async list(): Promise<Payment[]> {
      await mockDelay(300)
      return [...payments]
    },

    async listForCustomer(customerId: string): Promise<Payment[]> {
      await mockDelay(300)
      return payments.filter((p) => p.customerId === customerId)
    },

    async get(id: string): Promise<Payment> {
      await mockDelay(200)
      return { ...findOrThrow(id) }
    },

    async create(input: CreatePaymentInput): Promise<Payment> {
      await mockDelay(500)
      const now = new Date().toISOString()
      const customerName = payments.find(
        (p) => p.customerId === input.customerId
      )?.customerName
      const newPayment: Payment = {
        id: `pay-${Date.now()}`,
        tenantId: 'default',
        customerId: input.customerId,
        customerName,
        amount: input.amount,
        currency: input.currency,
        paymentMethod: input.paymentMethod,
        gateway: input.gateway,
        status: 'pending',
        transactionId: null,
        description: input.description ?? null,
        paidAt: null,
        createdAt: now,
        updatedAt: now,
      }
      payments = [newPayment, ...payments]
      return { ...newPayment }
    },

    async createOnlineOrder(
      input: OnlinePaymentInput,
      idempotencyKey: string
    ): Promise<RazorpayCheckoutOrder> {
      await mockDelay(300)
      const replay = onlineOrders.get(idempotencyKey)
      if (replay) return { ...replay, payment: { ...replay.payment } }

      const now = new Date().toISOString()
      const providerOrderId = `order_mock_${idempotencyKey}`
      const payment: Payment = {
        id: `pay-online-${Date.now()}`,
        tenantId: 'default',
        customerId: input.customerId,
        customerName: payments.find((p) => p.customerId === input.customerId)?.customerName,
        amount: input.amount,
        currency: input.currency,
        paymentMethod: input.paymentMethod,
        gateway: 'razorpay',
        status: 'pending',
        transactionId: null,
        providerOrderId,
        providerPaymentId: null,
        providerStatus: 'created',
        description: input.description ?? null,
        paidAt: null,
        createdAt: now,
        updatedAt: now,
      }
      const order: RazorpayCheckoutOrder = {
        payment,
        orderRecordId: `mock-${Date.now()}`,
        providerOrderId,
        keyId: 'rzp_test_taxhive_mock',
        amountMinor: Math.round(input.amount * 100),
        currency: input.currency,
        receipt: `client_${payment.id}`,
        orderStatus: 'created',
        expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
      }
      payments = [payment, ...payments]
      onlineOrders.set(idempotencyKey, order)
      return { ...order, payment: { ...payment } }
    },

    async verifyOnlinePayment(proof: RazorpayCheckoutProof): Promise<Payment> {
      await mockDelay(300)
      const current = payments.find(
        (payment) => payment.providerOrderId === proof.providerOrderId
      )
      if (!current) throw new Error('Provider order not found')
      const now = new Date().toISOString()
      const next: Payment = {
        ...current,
        status: 'completed',
        transactionId: proof.providerPaymentId,
        providerPaymentId: proof.providerPaymentId,
        providerStatus: 'captured',
        paidAt: now,
        updatedAt: now,
      }
      payments = payments.map((payment) => payment.id === next.id ? next : payment)
      return { ...next }
    },

    async createSubscriptionOrder(
      invoiceId: string,
      idempotencyKey: string
    ): Promise<RazorpaySubscriptionCheckoutOrder> {
      await mockDelay(250)
      const replay = subscriptionOrders.get(idempotencyKey)
      if (replay) return { ...replay }
      const existing = [...subscriptionOrders.values()].find(
        (order) => order.invoiceId === invoiceId
      )
      if (existing) return { ...existing }
      const invoice = getMockSubscriptionInvoice(invoiceId)
      if (!invoice || !invoice.totalMinor || !invoice.currency) {
        throw new Error('Subscription invoice pricing is unavailable')
      }
      const order: RazorpaySubscriptionCheckoutOrder = {
        orderRecordId: `subscription-${Date.now()}`,
        tenantId: invoice.tenantId,
        subscriptionId: invoice.subscriptionId,
        invoiceId,
        providerOrderId: `order_mock_subscription_${invoiceId}`,
        keyId: 'rzp_test_taxhive_mock',
        amountMinor: invoice.totalMinor,
        currency: invoice.currency,
        receipt: invoice.invoiceNumber,
        orderStatus: 'created',
        expiresAt: invoice.dueAt ?? null,
      }
      subscriptionOrders.set(idempotencyKey, order)
      return { ...order }
    },

    async verifySubscriptionPayment(
      proof: RazorpayCheckoutProof
    ): Promise<RazorpaySubscriptionPaymentResult> {
      await mockDelay(250)
      const order = [...subscriptionOrders.values()].find(
        (candidate) => candidate.providerOrderId === proof.providerOrderId
      )
      if (!order) throw new Error('Subscription provider order not found')
      settleMockSubscriptionInvoice(order.invoiceId, proof.providerPaymentId)
      return {
        orderRecordId: order.orderRecordId,
        tenantId: order.tenantId,
        subscriptionId: order.subscriptionId,
        invoiceId: order.invoiceId,
        providerOrderId: order.providerOrderId,
        providerPaymentId: proof.providerPaymentId,
        providerStatus: 'captured',
        orderStatus: 'paid',
        billingDeliveryStatus: 'completed',
      }
    },

    async reconcile(id: string): Promise<Payment> {
      await mockDelay(250)
      const current = findOrThrow(id)
      if (current.gateway !== 'razorpay') {
        throw new Error('Only Razorpay payments have provider status')
      }
      return { ...current }
    },

    async updateStatus(id: string, updates: UpdatePaymentInput): Promise<Payment> {
      await mockDelay(300)
      const current = findOrThrow(id)
      const next: Payment = {
        ...current,
        ...updates,
        transactionId: updates.transactionId ?? current.transactionId,
        description:
          updates.description !== undefined
            ? updates.description
            : current.description,
        updatedAt: new Date().toISOString(),
      }
      payments = payments.map((p) => (p.id === id ? next : p))
      return { ...next }
    },

    async process(id: string, transactionId: string): Promise<Payment> {
      await mockDelay(400)
      const current = findOrThrow(id)
      if (current.gateway !== 'manual') {
        throw new Error('Provider-managed payments require provider verification')
      }
      const now = new Date().toISOString()
      const next: Payment = {
        ...current,
        status: 'completed',
        transactionId,
        paidAt: now,
        updatedAt: now,
      }
      payments = payments.map((p) => (p.id === id ? next : p))
      return { ...next }
    },

    async fail(id: string, reason: string): Promise<Payment> {
      await mockDelay(400)
      const current = findOrThrow(id)
      if (current.gateway !== 'manual') {
        throw new Error('Provider-managed payments require provider verification')
      }
      const next: Payment = {
        ...current,
        status: 'failed',
        description: reason || current.description,
        updatedAt: new Date().toISOString(),
      }
      payments = payments.map((p) => (p.id === id ? next : p))
      return { ...next }
    },

    async getStats(): Promise<PaymentStats> {
      await mockDelay(250)
      return computeStats(payments)
    },
  }
}
